import Image from '@tiptap/extension-image';
import { NodeView } from '@tiptap/core';
import { Plugin, PluginKey } from 'prosemirror-state';
import { Decoration, DecorationSet } from 'prosemirror-view';

// Custom Image Extension with resizing capabilities
export const ResizableImage = Image.extend({
    name: 'resizableImage',

    addAttributes() {
        return {
            ...this.parent?.(),
            // Core attributes
            src: {
                default: null,
            },
            alt: {
                default: null,
            },
            title: {
                default: null,
            },
            // Sizing attributes
            width: {
                default: null,
                parseHTML: element => element.getAttribute('width'),
                renderHTML: attributes => {
                    if (!attributes.width) {
                        return {};
                    }
                    return { width: attributes.width };
                },
            },
            height: {
                default: null,
                parseHTML: element => element.getAttribute('height'),
                renderHTML: attributes => {
                    if (!attributes.height) {
                        return {};
                    }
                    return { height: attributes.height };
                },
            },
            // Style attributes
            display: {
                default: 'block',
                parseHTML: element => element.style.display,
                renderHTML: attributes => {
                    return { style: `display: ${attributes.display || 'block'}` };
                },
            },
            float: {
                default: null,
                parseHTML: element => element.style.float,
                renderHTML: attributes => {
                    if (!attributes.float) {
                        return {};
                    }
                    return { style: `float: ${attributes.float}` };
                },
            },
            margin: {
                default: '0 auto',
                parseHTML: element => element.style.margin,
                renderHTML: attributes => {
                    return { style: `margin: ${attributes.margin || '0 auto'}` };
                },
            },
            borderRadius: {
                default: null,
                parseHTML: element => element.style.borderRadius,
                renderHTML: attributes => {
                    if (!attributes.borderRadius) {
                        return {};
                    }
                    return { style: `border-radius: ${attributes.borderRadius}` };
                },
            },
            border: {
                default: null,
                parseHTML: element => element.style.border,
                renderHTML: attributes => {
                    if (!attributes.border) {
                        return {};
                    }
                    return { style: `border: ${attributes.border}` };
                },
            },
            // For keeping track of original dimensions
            originalWidth: {
                default: null,
                parseHTML: element => element.dataset.originalWidth,
                renderHTML: attributes => {
                    if (!attributes.originalWidth) {
                        return {};
                    }
                    return { 'data-original-width': attributes.originalWidth };
                },
            },
            originalHeight: {
                default: null,
                parseHTML: element => element.dataset.originalHeight,
                renderHTML: attributes => {
                    if (!attributes.originalHeight) {
                        return {};
                    }
                    return { 'data-original-height': attributes.originalHeight };
                },
            },
        };
    },

    addNodeView() {
        return ({ node, editor, getPos }) => {
            const dom = document.createElement('div');
            dom.classList.add('resizable-image-wrapper');

            // Create the image element
            const img = document.createElement('img');
            img.src = node.attrs.src;
            if (node.attrs.alt) img.alt = node.attrs.alt;
            if (node.attrs.title) img.title = node.attrs.title;
            img.draggable = false;

            // Apply dimensions if they exist
            if (node.attrs.width) img.style.width = node.attrs.width;
            if (node.attrs.height) img.style.height = 'auto';

            // Apply style attributes
            if (node.attrs.borderRadius) img.style.borderRadius = node.attrs.borderRadius;
            if (node.attrs.border) img.style.border = node.attrs.border;

            // Store original dimensions when the image loads
            img.onload = () => {
                if (!node.attrs.originalWidth) {
                    editor.commands.updateAttributes('resizableImage', {
                        originalWidth: img.naturalWidth,
                        originalHeight: img.naturalHeight,
                    });
                }
            };

            // Add the image to the wrapper
            dom.appendChild(img);

            // Add resize handles (4 corner handles only)
            const directions = ['ne', 'se', 'sw', 'nw'];
            directions.forEach(direction => {
                const handle = document.createElement('div');
                handle.classList.add('resize-handle', `handle-${direction}`);
                handle.dataset.direction = direction;
                dom.appendChild(handle);
            });

            // Set up event listeners for resizing
            let startX, startWidth, aspectRatio, isResizing = false;
            let currentDirection = '';

            // Find all handles
            const handles = dom.querySelectorAll('.resize-handle');

            handles.forEach(handle => {
                handle.addEventListener('mousedown', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    isResizing = true;
                    currentDirection = handle.dataset.direction;

                    startX = e.clientX;
                    startWidth = img.offsetWidth;
                    aspectRatio = img.offsetWidth / img.offsetHeight;

                    document.addEventListener('mousemove', handleResize);
                    document.addEventListener('mouseup', stopResize);

                    dom.classList.add('resizing');
                });
            });

            const handleResize = (e) => {
                if (!isResizing) return;

                const deltaX = e.clientX - startX;

                let newWidth = startWidth;
                if (currentDirection.includes('e')) newWidth = startWidth + deltaX;
                if (currentDirection.includes('w')) newWidth = startWidth - deltaX;

                newWidth = Math.max(40, newWidth);

                // Always maintain aspect ratio
                const newHeight = newWidth / aspectRatio;

                img.style.width = `${newWidth}px`;
                img.style.height = `${newHeight}px`;
            };

            const stopResize = () => {
                if (!isResizing) return;

                isResizing = false;
                currentDirection = '';

                document.removeEventListener('mousemove', handleResize);
                document.removeEventListener('mouseup', stopResize);

                dom.classList.remove('resizing');

                // Update the node attributes with new width (height is auto-computed)
                if (getPos && editor.isEditable) {
                    const currentAspectRatio = img.offsetWidth / img.offsetHeight;
                    editor.commands.command(({ tr }) => {
                        tr.setNodeMarkup(getPos(), null, {
                            ...node.attrs,
                            width: `${img.offsetWidth}px`,
                            height: `${Math.round(img.offsetWidth / currentAspectRatio)}px`,
                        });
                        return true;
                    });
                }
            };

            // Double click to reset to original dimensions
            const handleDblClick = () => {
                if (node.attrs.originalWidth && node.attrs.originalHeight) {
                    if (getPos && editor.isEditable) {
                        editor.commands.command(({ tr }) => {
                            tr.setNodeMarkup(getPos(), null, {
                                ...node.attrs,
                                width: `${node.attrs.originalWidth}px`,
                                height: `${node.attrs.originalHeight}px`,
                            });
                            return true;
                        });
                    }
                }
            };
            img.addEventListener('dblclick', handleDblClick);

            return {
                dom,
                update: (updatedNode) => {
                    if (updatedNode.type !== node.type) return false;

                    node = updatedNode;

                    // Update attributes
                    img.src = updatedNode.attrs.src;
                    if (updatedNode.attrs.alt) img.alt = updatedNode.attrs.alt;
                    if (updatedNode.attrs.title) img.title = updatedNode.attrs.title;

                    // Update width, keep height auto for proportion
                    if (updatedNode.attrs.width) img.style.width = updatedNode.attrs.width;
                    img.style.height = 'auto';

                    // Update style attributes
                    if (updatedNode.attrs.borderRadius) img.style.borderRadius = updatedNode.attrs.borderRadius;
                    if (updatedNode.attrs.border) img.style.border = updatedNode.attrs.border;

                    return true;
                },
                destroy: () => {
                    img.onload = null;
                    img.removeEventListener('dblclick', handleDblClick);
                },
            };
        };
    },

    addProseMirrorPlugins() {
        return [
            new Plugin({
                key: new PluginKey('resizableImagePlugin'),
                props: {
                    handleDOMEvents: {
                        mouseover(view, event) {
                            // Show resize handles when hovering over images
                            if (event.target.tagName === 'IMG' || event.target.closest('.resizable-image-wrapper')) {
                                const wrapper = event.target.closest('.resizable-image-wrapper');
                                if (wrapper) {
                                    wrapper.classList.add('hovered');
                                }
                            }
                        },
                        mouseout(view, event) {
                            // Hide resize handles when not hovering over images
                            const relatedTarget = event.relatedTarget;
                            if (
                                event.target.tagName === 'IMG' ||
                                event.target.closest('.resizable-image-wrapper')
                            ) {
                                if (!relatedTarget || !relatedTarget.closest('.resizable-image-wrapper')) {
                                    const wrapper = event.target.closest('.resizable-image-wrapper');
                                    if (wrapper && !wrapper.classList.contains('resizing')) {
                                        wrapper.classList.remove('hovered');
                                    }
                                }
                            }
                        },
                    },
                },
            }),
        ];
    },
});